import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { catchError, lastValueFrom, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ITranslateOptions, TranslateLangauge, TranslateLanguage, TranslateOptions } from './translate.interface';

/** คีย์จดจำภาษาใน local storage */
const STORAGE_KEY = 'multi-language';

/** ที่อยู่ของไฟล์ JSON (public/assets/languages) */
const ASSETS = 'assets/languages';

/** ภาษาที่ใช้เมื่อโหลดข้อมูลร้านค้าไม่ได้ */
const FALLBACK_OPTIONS: ITranslateOptions = { defaultLanguage: 'th', listOfLanguages: ['th', 'en', 'ch', 'ru'] };

/**
 * แปลภาษาแบบเดียวกับ APP-BO (shareds/translate) — ใช้ข้อความภาษาไทยเป็น key
 * และเก็บคำแปลไว้ที่ public/assets/languages/{languageId}.json แยกตาม module
 *
 * ต่างจาก APP-BO ตรงที่ใช้ signal (แอปนี้เป็น zoneless + SSR) ทำให้ pipe แปลแบบ sync ได้
 */
@Injectable({ providedIn: 'root' })
export class TranslateService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** ไม่ผ่าน interceptor (ไม่ต้องเติม base url / ไม่ให้ loading overlay ขึ้น) */
  private readonly http = new HttpClient(inject(HttpBackend));

  private readonly _languages = signal<TranslateLanguage[]>([]);
  private readonly _language = signal<TranslateLanguage | null>(this.readStoredLanguage());
  private readonly _translations = signal<Record<string, TranslateLangauge>>({});
  private readonly _loading = new Set<string>();
  private _loadedAppId?: string;

  /** module เริ่มต้น เมื่อไม่ได้ส่ง module มากับ pipe */
  module = '';

  /** รายการภาษาของร้าน */
  readonly languages = this._languages.asReadonly();

  /** ภาษาที่ใช้อยู่ */
  readonly language = this._language.asReadonly();

  /** คำแปลของภาษาปัจจุบัน */
  readonly translations = computed<TranslateLangauge>(() => {
    const lang = this._language();
    if (!lang || lang.isBase) return {};
    return this._translations()[lang.id] ?? {};
  });

  constructor() {
    const lang = this._language();
    if (lang) this.loadTranslateFile(lang);
  }

  /** เปลี่ยนภาษา */
  setLanguage(item: TranslateLanguage | null) {
    if (JSON.stringify(this._language()) === JSON.stringify(item)) return;
    this._language.set(item);
    if (this.isBrowser) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(item)); } catch { }
      document.documentElement.lang = item?.code === 'ch' ? 'zh' : (item?.code ?? 'th');
    }
    if (item) this.loadTranslateFile(item);
  }

  /**
   * โหลดรายการภาษาจาก languages.json และภาษาเริ่มต้นตาม languageDefault ของร้าน
   * เรียกครั้งเดียวต่อ appId
   */
  async loadLanguageItems(appId: string | null | undefined) {
    if (!this.isBrowser || !appId || this._loadedAppId === appId) return;
    this._loadedAppId = appId;

    // แสดงทุกภาษาใน languages.json ไม่กรองตาม languageList ของร้าน (ใช้แค่ languageDefault)
    const [options, all] = await Promise.all([this.loadShopOptions(appId), this.loadLanguageFile()]);
    const items = [...all].sort((a, b) => Number(b.isBase) - Number(a.isBase));
    this._languages.set(items);

    // ถ้ายังไม่เคยเลือกภาษา ใช้ภาษาหลักของร้าน / ถ้าภาษาที่เลือกไว้ร้านไม่รองรับ ใช้ภาษาแรก
    const current = this._language();
    const found = current && items.find(m => m.id === current.id);
    if (found) this.setLanguage(found);
    else if (!current && options.defaultLanguage)
      this.setLanguage(items.find(m => m.code.toLowerCase() === options.defaultLanguage.toLowerCase()) ?? items[0] ?? null);
    else this.setLanguage(items[0] ?? null);
  }

  /** แปลภาษาแบบ sync (ใช้ใน .ts ได้ เช่น ข้อความ toast) */
  instant(key: string | null | undefined, options?: TranslateOptions): string {
    if (!key) return '';
    const module = this.bindModule(options);
    const text = this.translations()[module]?.[key] || key;
    return this.bindOptions(text, options);
  }

  //#region Helpers

  /** แทนค่า {param} ในข้อความ */
  private bindOptions(text: string, options: TranslateOptions) {
    if (!options || typeof options !== 'object') return text;
    Object.entries(options).forEach(([key, value]) => {
      if (key === 'module') return;
      text = text.replace(new RegExp(`{\\s*${key}\\s*}`, 'g'), String(value ?? ''));
    });
    return text;
  }

  private bindModule(options: TranslateOptions) {
    if (typeof options === 'string') return options;
    if (options && typeof options === 'object' && options.module) return options.module;
    return this.module;
  }

  private readStoredLanguage(): TranslateLanguage | null {
    if (typeof localStorage === 'undefined') return null;
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch { return null; }
  }

  //#endregion

  //#region Load files

  private loadTranslateFile(lang: TranslateLanguage) {
    if (!this.isBrowser || lang.isBase) return;
    if (this._translations()[lang.id] || this._loading.has(lang.id)) return;
    this._loading.add(lang.id);
    this.http.get<TranslateLangauge>(`${ASSETS}/${lang.id}.json`, { params: { t: Date.now() } })
      .pipe(catchError(() => of({} as TranslateLangauge)))
      .subscribe(res => {
        this._loading.delete(lang.id);
        this._translations.update(m => ({ ...m, [lang.id]: res }));
      });
  }

  private loadLanguageFile() {
    return lastValueFrom(this.http
      .get<TranslateLanguage[]>(`${ASSETS}/languages.json`, { params: { t: Date.now() } })
      .pipe(catchError(() => of([] as TranslateLanguage[]))));
  }

  /** ภาษาที่ร้านค้าตั้งค่าไว้ (เหมือน SetupService.loadDefaultLanguage ของ APP-BO) */
  private loadShopOptions(appId: string) {
    return lastValueFrom(this.http
      .get<{ data: { languageDefault: string; languageList: string } }>(
        `${environment.apiURL}/api/Public/Setting/Business`, { headers: { 'www-app-id': appId } })
      .pipe(map(res => ({
        defaultLanguage: res.data.languageDefault || FALLBACK_OPTIONS.defaultLanguage,
        listOfLanguages: JSON.parse(res.data.languageList || '[]') as string[],
      } as ITranslateOptions)))
      .pipe(catchError(() => of(FALLBACK_OPTIONS))));
  }

  //#endregion
}
